import { styled } from "goober";
import { Skeleton } from "../Skeleton";
import {
  MONITORING_CARD_MAX,
  MONITORING_CARD_MIN,
  MONITORING_PANEL_GAP,
  MONITORING_PANEL_PADDING,
  MONITORING_SHORT_VIEWPORT_HEIGHT,
} from "./monitoringPanelLayout";

export function PanelSkeleton() {
  return (
    <Skeleton
      aria-hidden
      style={{ width: "100%", height: "100%", minHeight: 72, borderRadius: 12 }}
    />
  );
}

// Shared layout grid for the Workflows tab, so the real view and its loading /
// empty placeholders reflow identically.

export const Content = styled("div")`
  flex: 1;
  min-height: 0;
  padding: 0 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 900px) {
    flex: none;
  }
`;

export const WorkflowsShell = styled("div")`
  flex: 1;
  min-height: 0;
  padding: 0 12px 12px;
  display: flex;
  gap: 12px;
  align-items: stretch;

  @media (max-width: 900px) {
    flex-direction: column;
    flex: none;
  }

  @media (min-width: 901px) and (max-height: ${MONITORING_SHORT_VIEWPORT_HEIGHT}px) {
    overflow-x: auto;
  }
`;

// Single flat grid for the Workflows tab: donut + execution volume on row 1,
// storage + routes + the side stack on row 2. It collapses to one column that
// scrolls down when narrow, and to one row that scrolls across when short, so a
// card is never squeezed below a readable size (it moves off-screen instead).
export const WorkflowsGrid = styled("div")`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 12px;
  align-items: stretch;

  & > * {
    min-width: 0;
    min-height: 0;
  }

  /* Execution-volume panel spans the two columns beside the donut. */
  & > :nth-child(2) {
    grid-column: span 2;
  }

  /* Narrow: one column, scroll down (mirrors the height collapse below). */
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-rows: none;
    grid-auto-rows: minmax(${MONITORING_CARD_MIN}px, auto);
    flex: none;

    & > :nth-child(2) {
      grid-column: auto;
    }
  }

  @media (min-width: 901px) and (max-height: ${MONITORING_SHORT_VIEWPORT_HEIGHT}px) {
    display: flex;
    flex: none;

    & > * {
      flex: 0 0 ${MONITORING_CARD_MIN}px;
    }

    & > :nth-child(2) {
      flex: 0 0 auto;
      min-width: ${MONITORING_CARD_MIN}px;
      max-width: ${MONITORING_CARD_MAX}px;
    }
  }
`;

// AI tab grid: the overview strip spans the top, the two spend charts stack in
// column 1, and the models table fills columns 2–3. Same responsive collapse as
// WorkflowsGrid — one column that scrolls down when narrow, one row that scrolls
// across when short — so every panel, the strip included, becomes a card and
// moves off-screen at full size instead of shrinking.
export const AiGrid = styled("div")`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  /* row 1: overview strip (hugs); row 2: spend-vs-limits fills the leftover
     height; row 3: spend-by-model hugs its content. */
  grid-template-rows: auto 1fr auto;
  gap: 12px;
  align-items: stretch;

  & > * {
    min-width: 0;
    min-height: 0;
  }

  /* overview strip spans the full width across the top */
  & > :nth-child(1) {
    grid-column: 1 / span 3;
    grid-row: 1;
  }
  /* the two spend charts stack in column 1 */
  & > :nth-child(2) {
    grid-column: 1;
    grid-row: 2;
  }
  & > :nth-child(3) {
    grid-column: 1;
    grid-row: 3;
  }
  /* the models table fills columns 2–3 */
  & > :nth-child(4) {
    grid-column: 2 / span 2;
    grid-row: 2 / span 2;
  }

  /* Narrow: one column, scroll down. The strip keeps its natural height (the
     explicit auto row); the cards below take the readable min. */
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    grid-auto-rows: minmax(${MONITORING_CARD_MIN}px, auto);
    flex: none;

    & > :nth-child(1),
    & > :nth-child(2),
    & > :nth-child(3),
    & > :nth-child(4) {
      grid-column: auto;
      grid-row: auto;
    }
  }

  /* Short: one row, scroll across. Flex so cards size individually: the overview
     strip (1st) is thin (stacked stats + a full-width picker), the models table
     (4th) is wide, and the two spend charts keep the default width. */
  @media (min-width: 901px) and (max-height: ${MONITORING_SHORT_VIEWPORT_HEIGHT}px) {
    display: flex;
    overflow-x: auto;

    & > * {
      flex: 0 0 ${MONITORING_CARD_MIN}px;
    }

    & > :nth-child(1) {
      flex: 0 0 240px;
    }

    & > :nth-child(4) {
      flex: 0 0 ${MONITORING_CARD_MIN * 2 + 12}px;
    }
  }
`;

export const TopRow = styled("div")`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: minmax(0, 1fr);
  gap: 12px;
  align-items: stretch;
  flex: 1;
  min-height: 0;

  /* let panels shrink below their content so the charts (flex:1) compress to fit */
  & > * {
    min-height: 0;
  }

  /* the last panel spans the two columns beside the narrow first panel */
  & > *:last-child {
    grid-column: span 2;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    flex: none;

    & > *:last-child {
      grid-column: auto;
    }
  }
`;

export const SideStack = styled("div")`
  display: grid;
  grid-template-rows: repeat(3, minmax(0, 1fr));
  gap: 12px;
  min-width: 0;
  min-height: 0;
`;

// A single side-stack row split into two cards side by side.
export const SideStackPair = styled("div")`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  min-width: 0;
  min-height: 0;
`;

// Frame matching the chart panels (ActiveWorkflowsBySpace, StorageBySpace, …).
export const Panel = styled("div")`
  position: relative;
  min-width: 0;
  padding: ${MONITORING_PANEL_PADDING}px;
  border-radius: 12px;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  display: flex;
  flex-direction: column;
  gap: ${MONITORING_PANEL_GAP}px;
`;

export const PanelHeader = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const TotalRow = styled("div")`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
`;
